import React from "react";
import Spinner from "../../../components/Spinner/Spinner";
import { useSyncedScroll } from "../../../hooks/useSyncedScroll";
import type { DepGraph, DepGraphActivity } from "../../../models";
import DepTable, { type DepEditIntents } from "../depCore/DepTable/DepTable";
import { type ExternalCandidate } from "../depCore/DepChipTypeahead/DepChipTypeahead";
import { type ApplyEditArgs } from "../depCore/DepTable/EditActivityRow/EditActivityRow";
import { type InlineAddConfig } from "../depCore/DepTable/InlineAddRow/InlineAddRow";
import { buildAnchorLayout, collectAnchorKeys } from "../depCore/anchorLayout";
import {
  calendarFromActivities,
  calendarFromGraph,
  computeRowLayout,
  countDrivers,
  neighborRoles,
  NO_NEIGHBORS,
} from "../depCore/graphRows";
import SequenceModeBar from "./SequenceModeBar/SequenceModeBar";
import SequenceStripPane from "./SequenceStripPane/SequenceStripPane";
import RelationshipsPanel from "./RelationshipsPanel/RelationshipsPanel";
import {
  INSERT_KEY,
  injectLocalAdds,
  type LocalAddRow,
  type ViewMode,
} from "./sequenceHelpers";
import "./SequenceView.css";

export { collectAnchorKeys };
export type { LocalAddRow };

// The table is fixed-width with all columns; the gantt covers its right side.
// Drag the divider right to uncover Successors, left to cover more. (No column
// re-layout — the table never squishes.)

export interface Props {
  /** Board level rows (zone tuples, board order) — levels with no activities
   *  still render as empty groups so work can be added straight into them. */
  boardZones?: Array<{ a: string; b: string; c: string }>;
  /** "board" = show BOARD dates (no forward-pass display); "live" = the pass
   *  recalculates as you edit. */
  mode?: "board" | "live";
  onModeChange?: (m: "board" | "live") => void;
  /** Note shown under the Board-dates / Forward-pass toggle. */
  modeNote?: string;
  /** "Apply dates" action to the right of the toggle (board or train). */
  onApplyDates?: () => void;
  applyDatesLabel?: string;
  applyDatesNote?: string;
  /** Whether the forward pass holds dates the board doesn't have yet. true ⇒
   *  the Apply button lights up green; false ⇒ it greys out (nothing to apply).
   *  Undefined leaves it neutral (hosts that don't compute a diff, e.g. trains). */
  applyDatesActive?: boolean;
  /** Un-focus the currently focused level (drives the "Show all" scope button —
   *  which shares its slot with the "By date" order toggle). */
  onShowAll?: () => void;
  /** Board (sandbox) dates per graph id — what "board" mode displays. */
  boardDates?: Map<string, { start: string | null; finish: string | null }>;
  /** PROTOTYPE: group rows by WBS leaf instead of Takt zone. */
  wbsLeafOf?: (id: string) => { key: string; label: string; order: number } | null;
  /** Unified board drill-in: when set, collapse every group EXCEPT this leaf key
   *  so the gantt focuses the level the board has selected. null ⇒ show all. */
  focusLeaf?: string | null;
  /** Flat (train) mode: render one ungrouped list with NO zone/section group
   *  headers — the train is level-agnostic. */
  flat?: boolean;
  /** Host content rendered at the LEFT of the mode toolbar, so the gantt's title
   *  / focus chip / actions share ONE header row with Board-dates / Forward-pass
   *  / External-ties instead of stacking a second strip above. */
  headerLeft?: React.ReactNode;
  /** PROTOTYPE: activity id -> anchor direction for the explicit ⚓ tags. */
  anchorTags?: Map<string, "in" | "out" | "both">;
  /** "plan" — the full Dependencies editor. "anchors" — the OUTSIDE activities
   *  the section is tied to, as rows with editable tie chips and a per-row
   *  review checkmark. */
  variant?: "plan" | "anchors";
  graph: DepGraph | null;
  loading: boolean;
  error: string | null;
  readOnly: boolean;

  intents: DepEditIntents;
  /** Query-driven out-of-section search for the "+ add" tie popover. */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  onEditDuration: (graphId: string, days: number) => void;
  /** Apply an edit-in-place (✎): code / name / days for any in-scope activity.
   *  Optional — the dep modal omits it (duration is edited inline instead). */
  onApplyEdit?: (id: string, args: ApplyEditArgs) => void;
  /** Ask the host to delete these activities — it runs the outside-ties guard
   *  and stages the removal on confirm. Optional — the dep modal omits delete. */
  onRequestDelete?: (graphIds: string[]) => void;
  /** Open the repeat-floor flow for one level (the group header's ⧉). */
  onRepeatLevel?: (zoneKey: string) => void;
  /** Inline "+ add activity" ghost rows (one per level group + insert-below). */
  inlineAdd?: InlineAddConfig;
  /** Staged adds the graph doesn't carry yet (forward pass held while an
   *  editing session is open) — rendered in place with "—" dates. */
  localAdds?: LocalAddRow[];
  /** True while any add row is being edited. The host holds the forward pass
   *  until it flips false (Done / Cancel), then recalculates once. */
  onEditingHoldChange?: (holding: boolean) => void;
  /** Anchors variant: review state, keyed per outside tie ("predId>succId").
   *  A row's checkbox flips every key its external covers. */
  reviewedAnchors?: Set<string>;
  onToggleAnchorReview?: (key: string) => void;
  /** Full-screen the logic editor over the whole app (toolbar ⤢ toggle). */
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /** Surface "Driving only" as a toolbar pill (in the Apply-dates slot) instead
   *  of the chart's ⚙ gear — the dep modal, which has no board. */
  drivingOnlyInBar?: boolean;
}

const SequenceView: React.FC<Props> = ({
  mode = "live",
  onModeChange,
  modeNote,
  onApplyDates,
  applyDatesLabel,
  applyDatesNote,
  applyDatesActive,
  onShowAll,
  boardDates,
  boardZones,
  wbsLeafOf,
  focusLeaf,
  flat = false,
  headerLeft,
  anchorTags,
  variant = "plan",
  graph,
  loading,
  error,
  readOnly,
  intents,
  fetchExternals,
  onEditDuration,
  onApplyEdit,
  onRequestDelete,
  onRepeatLevel,
  inlineAdd,
  localAdds,
  onEditingHoldChange,
  reviewedAnchors,
  onToggleAnchorReview,
  fullscreen,
  onToggleFullscreen,
  drivingOnlyInBar = false,
}) => {
  const anchorsMode = variant === "anchors";
  const [viewMode, setViewMode] = React.useState<ViewMode>("zone");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [rowHeights, setRowHeights] = React.useState<number[]>([]);
  const [drivingOnly, setDrivingOnly] = React.useState(false);

  // Hold the last non-empty graph across debounced refetches so the table
  // never blanks mid-edit (same pattern as the dep-editor modal).
  const lastGraphRef = React.useRef<DepGraph | null>(null);
  if (graph && Object.keys(graph.activities).length > 0) lastGraphRef.current = graph;
  const fetchedGraph =
    graph && Object.keys(graph.activities).length > 0 ? graph : lastGraphRef.current;
  // BOARD mode: every in-scope activity renders at its board dates — the
  // sandbox is the truth; logic is shown but does not drive the dates.
  const displayGraph = React.useMemo(() => {
    if (!fetchedGraph || mode !== "board" || !boardDates) return fetchedGraph;
    const acts: typeof fetchedGraph.activities = {};
    for (const [id, a] of Object.entries(fetchedGraph.activities)) {
      const bd = boardDates.get(id);
      acts[id] = bd
        ? { ...a, start: bd.start, finish: bd.finish, drive_start: bd.start, drive_finish: bd.finish }
        : a;
    }
    return { ...fetchedGraph, activities: acts };
  }, [fetchedGraph, mode, boardDates]);
  const ready = displayGraph != null && Object.keys(displayGraph.activities).length > 0;

  // "N activities" beside the title — the section's own (in-scope) work, not the
  // outside drivers that ride along for context.
  const activityCount = React.useMemo(
    () =>
      displayGraph
        ? Object.values(displayGraph.activities).filter((a) => a.in_scope).length
        : 0,
    [displayGraph],
  );

  // A focused level is ALREADY a single chronological list, so "by date" is
  // meaningless there — and date mode has no groups for the focus filter to keep,
  // which would render nothing. Force grouped layout while focused.
  const effectiveViewMode = focusLeaf ? "zone" : viewMode;
  const withAddRows =
    !anchorsMode && Boolean(inlineAdd) && !readOnly && effectiveViewMode === "zone";
  // External ties (out-of-section / finished drivers) ride as mini-bars on the
  // rows they drive. OFF by default for a clean section-only view; the header
  // toggle brings them in when the outside connections matter.
  const [showDrivers, setShowDrivers] = React.useState(false);
  const driverCount = React.useMemo(
    () => (displayGraph ? countDrivers(displayGraph) : 0),
    [displayGraph],
  );
  const anchorLayout = React.useMemo(
    () => (anchorsMode && displayGraph ? buildAnchorLayout(displayGraph) : null),
    [anchorsMode, displayGraph],
  );
  const baseLayout = React.useMemo(() => {
    if (!displayGraph) return [];
    if (anchorLayout) return anchorLayout.layout;
    // boardZones (the grid's rows, display order) drives the group order so
    // this view's levels always match the board — including empty levels,
    // which get a group (header + add row in edit mode) at their board
    // position: an empty level is somewhere you ADD work, not a secret.
    return computeRowLayout(
      displayGraph, effectiveViewMode, showDrivers, withAddRows, boardZones, wbsLeafOf,
    );
  }, [displayGraph, effectiveViewMode, showDrivers, anchorLayout, withAddRows, boardZones, wbsLeafOf]);

  // Mid-table insert (MS-Project style): a row's ＋ opens an editing add row
  // right below it.
  const [insertAt, setInsertAt] = React.useState<{ anchorId: string } | null>(null);
  const [autoEditGhostKey, setAutoEditGhostKey] = React.useState<string | null>(null);
  const openInsertBelow = React.useCallback((activityId: string) => {
    setInsertAt({ anchorId: activityId });
    setAutoEditGhostKey(INSERT_KEY);
  }, []);

  // Editing-session hold: while ANY add/edit row is open, the host pauses the
  // forward pass; Done/Cancel releases it and the schedule recalculates once.
  const [editingKeys, setEditingKeys] = React.useState<Set<string>>(() => new Set());
  const handleGhostEditing = React.useCallback((key: string, editing: boolean) => {
    setEditingKeys((prev) => {
      if (prev.has(key) === editing) return prev;
      const next = new Set(prev);
      if (editing) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const holding = editingKeys.size > 0;
  const holdChangeRef = React.useRef(onEditingHoldChange);
  holdChangeRef.current = onEditingHoldChange;
  React.useEffect(() => {
    holdChangeRef.current?.(holding);
  }, [holding]);

  const fullLayout = React.useMemo(() => {
    const pendingLocals = (localAdds ?? []).filter(
      (a) => !displayGraph?.activities[a.draftId],
    );
    const woven =
      withAddRows && pendingLocals.length > 0
        ? injectLocalAdds(baseLayout, pendingLocals)
        : baseLayout;
    if (!insertAt || !withAddRows || !displayGraph) return woven;
    const ai = woven.findIndex(
      (x) => x.kind === "activity" && x.activity.id === insertAt.anchorId,
    );
    if (ai < 0) return woven;
    const ae = ai;
    const anchorEntry = woven[ai];
    const anchorNode =
      anchorEntry.kind === "activity" ? anchorEntry.activity : null;
    let nextActivityId: string | null = null;
    for (let i = ae + 1; i < woven.length; i++) {
      const e = woven[i];
      if (e.kind === "header" || e.kind === "ghost") break;
      if (e.kind === "activity") {
        nextActivityId = e.activity.id;
        break;
      }
    }
    const out = [...woven];
    out.splice(ae + 1, 0, {
      kind: "ghost",
      key: INSERT_KEY,
      zone: anchorNode
        ? { a: anchorNode.zone.a, b: anchorNode.zone.b, c: anchorNode.zone.c }
        : null,
      lastActivityId: insertAt.anchorId,
      nextActivityId,
      rowIndex: 0,
    });
    return out;
  }, [baseLayout, localAdds, insertAt, withAddRows, displayGraph]);

  // Collapse whole levels to read the big picture, expand one to focus on it.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => new Set());
  const groupKeys = React.useMemo(
    () => fullLayout.filter((e) => e.kind === "header").map((e) => e.key),
    [fullLayout],
  );
  const toggleGroup = React.useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const allCollapsed = groupKeys.length > 0 && groupKeys.every((k) => collapsed.has(k));
  const toggleAllGroups = React.useCallback(() => {
    setCollapsed((prev) => {
      const every = groupKeys.length > 0 && groupKeys.every((k) => prev.has(k));
      return every ? new Set() : new Set(groupKeys);
    });
  }, [groupKeys]);
  // Drop the member rows of collapsed groups, then renumber so both panes index
  // the same visible rows. When the board has drilled into a level (focusLeaf),
  // render ONLY that level's rows — no group headers at all (the header chip
  // already names the level) and nothing from the other levels. Group header
  // keys are `h-<leafKey>` (see computeRowLayout).
  const rowLayout = React.useMemo(() => {
    const out: typeof fullLayout = [];
    // Flat (train) mode: one ungrouped list — drop the zone/section group headers
    // entirely (the train is level-agnostic, so "no zone yet" is meaningless).
    if (flat) {
      for (const entry of fullLayout) {
        if (entry.kind === "header") continue;
        out.push(entry);
      }
      return out.map((e, i) => ({ ...e, rowIndex: i }));
    }
    if (focusLeaf != null) {
      const focusKey = `h-${focusLeaf}`;
      let inFocus = false;
      for (const entry of fullLayout) {
        if (entry.kind === "header") {
          inFocus = entry.key === focusKey;
          continue;
        }
        if (inFocus) out.push(entry);
      }
      return out.map((e, i) => ({ ...e, rowIndex: i }));
    }
    let hiding = false;
    for (const entry of fullLayout) {
      if (entry.kind === "header") {
        hiding = collapsed.has(entry.key);
        out.push(entry);
        continue;
      }
      if (!hiding) out.push(entry);
    }
    return out.map((e, i) => ({ ...e, rowIndex: i }));
  }, [fullLayout, collapsed, focusLeaf, flat]);

  // Anchors rows are externals — often outside the section's own window — so
  // that axis spans the DISPLAYED rows' dates, not the canvas activities'.
  const calendar = React.useMemo(() => {
    if (!displayGraph) return [];
    if (anchorLayout) {
      const acts: DepGraphActivity[] = [];
      for (const e of anchorLayout.layout) {
        if (e.kind === "activity") acts.push(e.activity);
      }
      return calendarFromActivities(acts);
    }
    return calendarFromGraph(displayGraph);
  }, [displayGraph, anchorLayout]);

  // Prune the single selection if its activity left the graph (deleted, undone).
  React.useEffect(() => {
    if (!displayGraph) return;
    if (selectedId && !displayGraph.activities[selectedId]) setSelectedId(null);
  }, [displayGraph, selectedId]);

  // Keep a row selected so the Relationships panel is never empty: default to
  // the first activity in view. This is an ordinary selection, so its arrows
  // highlight too — same behavior as clicking it.
  const firstActivityId = React.useMemo(() => {
    for (const e of rowLayout) if (e.kind === "activity") return e.activity.id;
    return null;
  }, [rowLayout]);
  React.useEffect(() => {
    if (anchorsMode || flat) return;
    if (selectedId != null) return;
    if (firstActivityId) setSelectedId(firstActivityId);
  }, [anchorsMode, flat, selectedId, firstActivityId]);

  const handleSelect = React.useCallback(
    (id: string) => setSelectedId((curr) => (curr === id ? null : id)),
    [],
  );

  // Selecting a row tints its direct predecessors light red and successors light
  // yellow on both panes. Skipped in anchors mode (its rows are grouped in/out).
  const neighbors = React.useMemo(
    () =>
      displayGraph && !anchorsMode
        ? neighborRoles(displayGraph, selectedId)
        : NO_NEIGHBORS,
    [displayGraph, anchorsMode, selectedId],
  );

  const handleRowHeights = React.useCallback((heights: number[]) => {
    setRowHeights(heights);
  }, []);

  const tableScrollRef = React.useRef<HTMLDivElement | null>(null);
  // Draggable split: the table is FIXED-WIDTH and the divider only sets how many
  // px of it show — past the table's own columns the gantt simply COVERS the
  // rest (the table never resizes or squishes). Clamped so you can't drag past
  // the table's right edge (no empty gap). Default shows through Preds; drag
  // right to uncover Successors, left to cover more.
  const [tableW, setTableW] = React.useState(640);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const startSplit = (e: React.MouseEvent) => {
    e.preventDefault();
    const root = bodyRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const onMove = (ev: MouseEvent) => {
      const full = tableScrollRef.current?.scrollWidth ?? 9999;
      const px = ev.clientX - rect.left;
      setTableW(Math.max(260, Math.min(full, px)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const stripScrollRef = React.useRef<HTMLDivElement | null>(null);
  useSyncedScroll(tableScrollRef, stripScrollRef, ready);

  // Preserve vertical scroll across refetches (layout recomputes from a fresh
  // graph object after every edit).
  const savedScrollRef = React.useRef(0);
  const handleTableScroll = React.useCallback(() => {
    if (tableScrollRef.current) savedScrollRef.current = tableScrollRef.current.scrollTop;
  }, []);
  React.useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (el && savedScrollRef.current && el.scrollTop !== savedScrollRef.current) {
      el.scrollTop = savedScrollRef.current;
    }
  }, [rowLayout]);

  // The detected loop, as row + tie lookups for highlighting.
  const cycleSets = React.useMemo(() => {
    const acts = displayGraph?.cycle_activities ?? [];
    if (acts.length === 0) return null;
    const nodes = new Set(acts.map((c) => c.id));
    const edges = new Set<string>();
    for (let i = 0; i < acts.length; i++) {
      edges.add(`${acts[i].id}>${acts[(i + 1) % acts.length].id}`);
    }
    return { nodes, edges };
  }, [displayGraph?.cycle_activities]);

  // Anchors mode with no rows: no live outside ties and no severed ones —
  // drop the empty panes instead of a blank table.
  const anchorsEmpty = anchorsMode && ready && baseLayout.length === 0;

  return (
    <div className="sequence-view">
      {anchorsMode && ready && !anchorsEmpty && (
        <div className="sequence-view-anchors-intro">
          <div className="sequence-view-anchors-note">
            ⚓ The outside activities this section is tied to — work in other
            sections and finished work, split into incoming (feeds work here)
            and outgoing (waits on work here). Each is shown with the section
            activities it connects to. The "In-section ties" column carries
            each row's ties into the section — an incoming row's successors,
            an outgoing row's preds. Add and edit ties there, and check the
            row off once its connections make sense for the new plan.
          </div>
        </div>
      )}
      {!anchorsMode && (headerLeft || onModeChange) && (
        <SequenceModeBar
          headerLeft={headerLeft}
          activityCount={activityCount}
          mode={mode}
          onModeChange={onModeChange}
          modeNote={modeNote}
          onApplyDates={onApplyDates}
          applyDatesLabel={applyDatesLabel}
          applyDatesNote={applyDatesNote}
          applyDatesActive={applyDatesActive}
          drivingOnly={drivingOnly}
          onToggleDrivingOnly={
            drivingOnlyInBar ? () => setDrivingOnly((o) => !o) : undefined
          }
          viewMode={viewMode}
          onViewModeChange={flat ? undefined : setViewMode}
          focused={Boolean(focusLeaf)}
          onShowAll={onShowAll}
          driverCount={driverCount}
          showDrivers={showDrivers}
          onToggleShowDrivers={() => setShowDrivers((o) => !o)}
          fullscreen={fullscreen}
          onToggleFullscreen={onToggleFullscreen}
        />
      )}
      {/* Everything below the toolbar header lives in ONE clipped region so that
          collapsing the pane to its header hides the body AND the Relationships
          strip together — nothing peeks below the header's bottom line. */}
      <div className="sequence-view-content">
      {displayGraph?.cycle_error && (
        <div className="sequence-view-cycle-warn">
          <b>Dependency loop detected</b> — the schedule pass can't run; dates
          shown are stored values. Break the loop by removing one of these
          ties:{" "}
          {(displayGraph.cycle_activities ?? [])
            .map((c) => `${c.task_code ? c.task_code + " · " : ""}${c.name}`)
            .join(" → ") || "(loop members unavailable)"}
        </div>
      )}
      <div className="sequence-view-body" ref={bodyRef}>
        {!ready && loading && (
          <div className="sequence-view-status">
            <Spinner visible label="Loading the plan..." />
          </div>
        )}
        {!ready && error && !loading && (
          <div className="sequence-view-status sequence-view-error">{error}</div>
        )}
        {!ready && !loading && !error && (
          <div className="sequence-view-status">
            {anchorsMode
              ? "No outside connections touch this section."
              : "No activities in this section yet."}
          </div>
        )}
        {anchorsEmpty && (
          <div className="sequence-view-status">
            No outside connections touch this section.
          </div>
        )}
        {ready && !anchorsEmpty && displayGraph && (
          <>
            <div
              className={
                "sequence-view-table-pane" +
                (anchorsMode ? " sequence-view-table-pane--anchors" : "")
              }
              style={anchorsMode ? undefined : { width: tableW, flex: "none" }}
              ref={tableScrollRef}
              onScroll={handleTableScroll}
            >
              <DepTable
                anchorTags={anchorTags}
                cycleNodes={cycleSets?.nodes}
                cycleEdges={cycleSets?.edges}
                graph={displayGraph}
                compact={!anchorsMode}
                fixedWidth={!anchorsMode}
                rowLayout={rowLayout}
                intents={intents}
                selectedId={selectedId}
                predIds={neighbors.preds}
                succIds={neighbors.succs}
                onSelect={handleSelect}
                onRowHeightsChange={handleRowHeights}
                readOnly={readOnly}
                onEditDuration={readOnly || anchorsMode ? undefined : onEditDuration}
                onApplyEdit={readOnly || anchorsMode ? undefined : onApplyEdit}
                onDeleteActivity={
                  readOnly || anchorsMode || !onRequestDelete
                    ? undefined
                    : (id: string) => onRequestDelete([id])
                }
                collapsedGroups={collapsed}
                onToggleGroup={effectiveViewMode === "zone" ? toggleGroup : undefined}
                onToggleAllGroups={
                  effectiveViewMode === "zone" && groupKeys.length > 1
                    ? toggleAllGroups
                    : undefined
                }
                allGroupsCollapsed={allCollapsed}
                onRepeatLevel={readOnly || anchorsMode ? undefined : onRepeatLevel}
                inlineAdd={readOnly || anchorsMode ? undefined : inlineAdd}
                autoEditGhostKey={autoEditGhostKey}
                onAutoEditConsumed={() => setAutoEditGhostKey(null)}
                onInsertBelow={withAddRows ? openInsertBelow : undefined}
                insertGhostKey={INSERT_KEY}
                onInsertDismiss={() => setInsertAt(null)}
                onInsertCommitted={(draftId) => setInsertAt({ anchorId: draftId })}
                onGhostEditingChange={handleGhostEditing}
                allowExternalEdits={!readOnly}
                fetchExternals={fetchExternals}
                anchorRowKeys={anchorLayout?.rowKeys}
                anchorDirections={anchorLayout?.directions}
                severedInfo={anchorLayout?.severedInfo}
                reviewedAnchors={anchorsMode ? reviewedAnchors : undefined}
                onToggleAnchorReview={
                  anchorsMode && !readOnly ? onToggleAnchorReview : undefined
                }
              />
            </div>
            <div
              className="sequence-view-divider"
              title="Drag to resize the panes"
              onMouseDown={startSplit}
            />
            <SequenceStripPane
              rowLayout={rowLayout}
              rowHeights={rowHeights}
              calendar={calendar}
              graph={displayGraph}
              stripScrollRef={stripScrollRef}
              selectedId={selectedId}
              predIds={neighbors.preds}
              succIds={neighbors.succs}
              onSelect={handleSelect}
              drivingOnly={drivingOnly}
              onDrivingOnlyChange={setDrivingOnly}
              hideSettings={drivingOnlyInBar}
            />
          </>
        )}
      </div>
      {!anchorsMode && !flat && ready && displayGraph && (
        <RelationshipsPanel
          graph={displayGraph}
          selectedId={selectedId}
          intents={intents}
          fetchExternals={fetchExternals}
          allowExternalEdits={!readOnly}
          readOnly={readOnly}
        />
      )}
      </div>
    </div>
  );
};

export default SequenceView;
